import React, { useCallback, useEffect, useRef, useState } from "react";
import { makeStyles } from "@material-ui/core/styles";
import { Button, CircularProgress, Dialog, IconButton, Typography } from "@material-ui/core";
import {
	Close as CloseIcon,
	ChevronLeft as ChevronLeftIcon,
	ChevronRight as ChevronRightIcon,
	Mic as MicIcon,
} from "@material-ui/icons";

import api from "../../services/api";
import ContactAvatar from "../ContactAvatar";
import { i18n } from "../../translate/i18n";
import { formatWhatsappMoment, markStorySeen } from "../../helpers/contactWhatsappStatus";

// Imagem e texto ficam 5 s na tela, como no WhatsApp; vídeo e áudio, a duração.
const STILL_DURATION_MS = 5000;
const TICK_MS = 50;
const DEFAULT_TEXT_BACKGROUND = "#1F7A6B";

// Fontes do status de texto (proto ExtendedTextMessage.FontType).
const STORY_FONTS = {
	0: "Helvetica, Arial, sans-serif",
	1: "Georgia, 'Times New Roman', serif",
	2: "'Brush Script MT', 'Segoe Script', cursive",
	3: "'Comic Sans MS', 'Chalkboard SE', cursive",
	4: "Impact, 'Arial Narrow', sans-serif",
	5: "'Arial Black', Impact, sans-serif",
};

const useStyles = makeStyles(() => ({
	paper: {
		backgroundColor: "#111",
		color: "#fff",
		width: "100%",
		maxWidth: 440,
		height: "min(90vh, 780px)",
		margin: 16,
		overflow: "hidden",
		display: "flex",
		flexDirection: "column",
		position: "relative",
	},
	bars: {
		display: "flex",
		gap: 4,
		padding: "10px 12px 0",
	},
	bar: {
		flex: 1,
		height: 3,
		borderRadius: 2,
		backgroundColor: "rgba(255,255,255,0.35)",
		overflow: "hidden",
	},
	barFill: {
		height: "100%",
		backgroundColor: "#fff",
	},
	header: {
		display: "flex",
		alignItems: "center",
		gap: 10,
		padding: "10px 8px 10px 12px",
	},
	headerText: {
		flex: 1,
		minWidth: 0,
	},
	name: {
		fontWeight: 600,
		whiteSpace: "nowrap",
		overflow: "hidden",
		textOverflow: "ellipsis",
	},
	when: {
		color: "rgba(255,255,255,0.7)",
		fontSize: 12,
	},
	stage: {
		flex: 1,
		minHeight: 0,
		position: "relative",
		display: "flex",
		alignItems: "center",
		justifyContent: "center",
	},
	media: {
		maxWidth: "100%",
		maxHeight: "100%",
		objectFit: "contain",
		display: "block",
	},
	blurred: {
		width: "100%",
		height: "100%",
		objectFit: "cover",
		filter: "blur(12px)",
		opacity: 0.6,
		position: "absolute",
		inset: 0,
	},
	textStory: {
		width: "100%",
		height: "100%",
		display: "flex",
		alignItems: "center",
		justifyContent: "center",
		padding: 32,
		textAlign: "center",
		fontSize: 26,
		lineHeight: 1.35,
		whiteSpace: "pre-wrap",
		wordBreak: "break-word",
		overflowY: "auto",
	},
	audioStory: {
		width: "100%",
		height: "100%",
		display: "flex",
		flexDirection: "column",
		alignItems: "center",
		justifyContent: "center",
		gap: 24,
		padding: 24,
	},
	audioIcon: {
		fontSize: 72,
		opacity: 0.9,
	},
	caption: {
		padding: "12px 16px 16px",
		textAlign: "center",
		whiteSpace: "pre-wrap",
		wordBreak: "break-word",
		maxHeight: "25%",
		overflowY: "auto",
		backgroundColor: "rgba(0,0,0,0.4)",
	},
	navLeft: {
		position: "absolute",
		left: 4,
		top: "50%",
		transform: "translateY(-50%)",
		color: "#fff",
		backgroundColor: "rgba(0,0,0,0.35)",
		zIndex: 2,
		"&:hover": { backgroundColor: "rgba(0,0,0,0.55)" },
	},
	navRight: {
		position: "absolute",
		right: 4,
		top: "50%",
		transform: "translateY(-50%)",
		color: "#fff",
		backgroundColor: "rgba(0,0,0,0.35)",
		zIndex: 2,
		"&:hover": { backgroundColor: "rgba(0,0,0,0.55)" },
	},
	centered: {
		position: "absolute",
		inset: 0,
		display: "flex",
		flexDirection: "column",
		alignItems: "center",
		justifyContent: "center",
		gap: 12,
		zIndex: 1,
		textAlign: "center",
		padding: 24,
	},
	retryButton: {
		color: "#fff",
		borderColor: "rgba(255,255,255,0.6)",
	},
	closeButton: {
		color: "#fff",
	},
}));

/**
 * Visualizador dos status de 24h do contato, no formato do WhatsApp: barras de
 * progresso no topo, avanço automático, setas e teclado (← → Esc).
 * A mídia é baixada do WhatsApp sob demanda, um story por vez.
 */
const ContactStoriesViewer = ({ open, onClose, stories, contact, ticketId }) => {
	const classes = useStyles();
	const [index, setIndex] = useState(0);
	const [progress, setProgress] = useState(0);
	const [mediaUrls, setMediaUrls] = useState({});
	const [loadingId, setLoadingId] = useState(null);
	const [errorId, setErrorId] = useState(null);
	const mediaUrlsRef = useRef({});
	const mediaElementRef = useRef(null);
	// O cabeçalho re-renderiza a cada presença ("digitando...") e recria o
	// onClose; via ref, o relógio do story não reinicia por causa disso.
	const onCloseRef = useRef(onClose);
	onCloseRef.current = onClose;

	const story = stories[index];
	// Dependências pelo id: a lista é recarregada (novo story, refresh de 5 min)
	// e os objetos mudam de identidade sem o story mudar.
	const storyId = story?.id;
	const storyRef = useRef(story);
	storyRef.current = story;
	const isStill = !!story && (story.type === "image" || story.type === "text");
	const isImage = story?.type === "image";
	const mediaUrl = story ? mediaUrls[story.id] : null;

	const goNext = useCallback(() => {
		setProgress(0);
		if (index >= stories.length - 1) {
			onCloseRef.current();
			return;
		}
		setIndex(index + 1);
	}, [index, stories.length]);

	const goPrevious = useCallback(() => {
		setProgress(0);
		setIndex(current => Math.max(0, current - 1));
	}, []);

	// O contato apagou um story enquanto a lista estava aberta.
	useEffect(() => {
		if (stories.length > 0 && index > stories.length - 1) setIndex(stories.length - 1);
	}, [stories.length, index]);

	// Libera os blobs ao fechar.
	useEffect(
		() => () => {
			Object.values(mediaUrlsRef.current).forEach(url => URL.revokeObjectURL(url));
		},
		[]
	);

	const loadMedia = useCallback(
		async target => {
			if (!target || target.type === "text" || mediaUrlsRef.current[target.id]) return;
			setLoadingId(target.id);
			setErrorId(null);
			try {
				const { data } = await api.get(`/tickets/${ticketId}/contact-stories/${encodeURIComponent(target.id)}/media`, {
					responseType: "blob",
				});
				const url = URL.createObjectURL(data);
				mediaUrlsRef.current = { ...mediaUrlsRef.current, [target.id]: url };
				setMediaUrls(mediaUrlsRef.current);
			} catch (err) {
				setErrorId(target.id);
			} finally {
				setLoadingId(current => (current === target.id ? null : current));
			}
		},
		[ticketId]
	);

	useEffect(() => {
		if (!storyId) return;
		markStorySeen(storyRef.current);
		loadMedia(storyRef.current);
	}, [storyId, loadMedia]);

	// Imagem e texto: relógio próprio (a imagem só conta depois de carregada).
	useEffect(() => {
		if (!storyId || !isStill) return undefined;
		if (isImage && !mediaUrl && errorId !== storyId) return undefined;

		const startedAt = Date.now();
		const timer = setInterval(() => {
			const ratio = (Date.now() - startedAt) / STILL_DURATION_MS;
			if (ratio >= 1) {
				clearInterval(timer);
				goNext();
			} else {
				setProgress(ratio);
			}
		}, TICK_MS);
		return () => clearInterval(timer);
	}, [storyId, isStill, isImage, mediaUrl, errorId, goNext]);

	useEffect(() => {
		if (!open) return undefined;
		const onKey = event => {
			if (event.key === "ArrowRight") goNext();
			if (event.key === "ArrowLeft") goPrevious();
		};
		window.addEventListener("keydown", onKey);
		return () => window.removeEventListener("keydown", onKey);
	}, [open, goNext, goPrevious]);

	const handleTimeUpdate = () => {
		const element = mediaElementRef.current;
		if (element && element.duration) {
			setProgress(element.currentTime / element.duration);
		}
	};

	const renderStage = () => {
		if (!story) return null;

		if (story.type === "text") {
			return (
				<div
					className={classes.textStory}
					style={{
						backgroundColor: story.backgroundColor || DEFAULT_TEXT_BACKGROUND,
						color: story.textColor || "#fff",
						fontFamily: STORY_FONTS[story.font] || STORY_FONTS[0],
					}}
				>
					{story.caption}
				</div>
			);
		}

		const failed = errorId === story.id;
		const loading = loadingId === story.id && !mediaUrl;

		return (
			<>
				{story.thumbnail && !mediaUrl && <img src={story.thumbnail} alt="" className={classes.blurred} />}
				{(loading || failed) && (
					<div className={classes.centered}>
						{loading && <CircularProgress size={36} style={{ color: "#fff" }} />}
						{failed && (
							<>
								<Typography>{i18n.t("contactWhatsappStatus.stories.loadError")}</Typography>
								<Button
									variant="outlined"
									size="small"
									className={classes.retryButton}
									onClick={() => loadMedia(story)}
								>
									{i18n.t("contactWhatsappStatus.stories.retry")}
								</Button>
							</>
						)}
					</div>
				)}
				{mediaUrl && story.type === "image" && <img src={mediaUrl} alt="" className={classes.media} />}
				{mediaUrl && story.type === "video" && (
					<video
						key={story.id}
						ref={mediaElementRef}
						src={mediaUrl}
						className={classes.media}
						autoPlay
						playsInline
						controls
						onTimeUpdate={handleTimeUpdate}
						onEnded={goNext}
					/>
				)}
				{story.type === "audio" && (
					<div
						className={classes.audioStory}
						style={{ backgroundColor: story.backgroundColor || DEFAULT_TEXT_BACKGROUND }}
					>
						<MicIcon className={classes.audioIcon} />
						{mediaUrl && (
							<audio
								key={story.id}
								ref={mediaElementRef}
								src={mediaUrl}
								autoPlay
								controls
								onTimeUpdate={handleTimeUpdate}
								onEnded={goNext}
							/>
						)}
					</div>
				)}
			</>
		);
	};

	return (
		<Dialog
			open={open}
			onClose={onClose}
			classes={{ paper: classes.paper }}
			// O avatar mora dentro do cabeçalho clicável do ticket: sem isto o
			// clique no visualizador sobe pela árvore do React e abre a gaveta.
			onClick={event => event.stopPropagation()}
		>
			<div className={classes.bars}>
				{stories.map((item, itemIndex) => (
					<div key={item.id} className={classes.bar}>
						<div
							className={classes.barFill}
							style={{
								width: `${itemIndex < index ? 100 : itemIndex === index ? Math.round(progress * 100) : 0}%`,
							}}
						/>
					</div>
				))}
			</div>
			<div className={classes.header}>
				<ContactAvatar contact={contact} style={{ width: 36, height: 36 }} />
				<div className={classes.headerText}>
					<Typography className={classes.name} title={contact?.name}>
						{contact?.name || contact?.number}
					</Typography>
					<Typography className={classes.when}>{story ? formatWhatsappMoment(story.postedAt) : ""}</Typography>
				</div>
				<IconButton
					className={classes.closeButton}
					onClick={onClose}
					aria-label={i18n.t("contactWhatsappStatus.stories.close")}
				>
					<CloseIcon />
				</IconButton>
			</div>
			<div className={classes.stage}>
				{index > 0 && (
					<IconButton
						className={classes.navLeft}
						size="small"
						onClick={goPrevious}
						aria-label={i18n.t("contactWhatsappStatus.stories.previous")}
					>
						<ChevronLeftIcon />
					</IconButton>
				)}
				{renderStage()}
				<IconButton
					className={classes.navRight}
					size="small"
					onClick={goNext}
					aria-label={i18n.t("contactWhatsappStatus.stories.next")}
				>
					<ChevronRightIcon />
				</IconButton>
			</div>
			{story?.caption && story.type !== "text" && (
				<Typography className={classes.caption}>{story.caption}</Typography>
			)}
		</Dialog>
	);
};

export default ContactStoriesViewer;
